import { useAppKit } from '@reown/appkit/react'
import { useAppKitWallet, Wallet } from '@reown/appkit-wallet-button/react'
import COINBASE_ICON from 'assets/wallets/coinbase-icon.svg'
import LEDGER_ICON from 'assets/wallets/ledger-icon.svg'
import METAMASK_ICON from 'assets/wallets/metamask-icon.svg'
import TRUST_WALLET_ICON from 'assets/wallets/trustwallet-icon.svg'
import WALLET_CONNECT_ICON from 'assets/wallets/walletconnect-icon.svg'
import IconButton from 'components/AccountDrawer/IconButton'
import { AutoColumn } from 'components/Column'
import { AutoRow } from 'components/Row'
import { getConnection } from 'connection'
import { ActivationStatus, useActivationState } from 'connection/activate'
import { ConnectionType } from 'connection/types'
import { useCallback } from 'react'
import { Grid, Link2, Settings } from 'react-feather'
import { reownAppKit } from 'reown/appKit'
import styled from 'styled-components/macro'
import { ThemedText } from 'theme'
import { flexColumnNoWrap } from 'theme/styles'

import PrivacyPolicyNotice from './PrivacyPolicyNotice'

const Wrapper = styled.div`
  ${flexColumnNoWrap};
  background-color: ${({ theme }) => theme.backgroundSurface};
  width: 100%;
  padding: 18px 16px 16px;
  flex: 1;
`

const OptionGrid = styled.div`
  display: grid;
  grid-gap: 10px;
  grid-template-columns: 1fr;
`

const ConnectionPanel = styled.div`
  display: grid;
  gap: 10px;
  padding: 10px;
  background-color: ${({ theme }) => theme.backgroundModule};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 12px;
`

const WalletOptionButton = styled.button`
  align-items: center;
  background-color: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 10px;
  color: ${({ theme }) => theme.textPrimary};
  cursor: pointer;
  display: grid;
  font-size: 16px;
  font-weight: 600;
  gap: 10px;
  grid-template-columns: 28px 1fr 28px;
  min-height: 40px;
  padding: 8px 12px;
  transition: ${({ theme }) => theme.transition.duration.fast};
  width: 100%;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  &:hover:enabled,
  &:focus:enabled {
    background-color: ${({ theme }) => theme.hoverState};
  }
`

const IconSlot = styled.span`
  align-items: center;
  background-color: ${({ theme }) => theme.backgroundModule};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 8px;
  color: ${({ theme }) => theme.textSecondary};
  display: flex;
  height: 24px;
  justify-content: center;
  opacity: 0.85;
  width: 24px;
`

const WalletLogo = styled.img`
  border-radius: 6px;
  display: block;
  height: 18px;
  object-fit: contain;
  width: 18px;
`

const BrandMark = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.textSecondary};
  display: flex;
  font-size: 8px;
  font-weight: 700;
  height: 18px;
  justify-content: center;
  line-height: 1;
  opacity: 0.8;
  width: 18px;
`

const WalletLabel = styled.span`
  overflow: hidden;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const PrivacyPolicyWrapper = styled.div`
  padding: 0 4px;
`

const REOWN_WALLETS: { wallet: Wallet; label: string; image?: string; mark?: string }[] = [
  { wallet: 'walletConnect', label: 'WalletConnect', image: WALLET_CONNECT_ICON },
  { wallet: 'coinbase', label: 'Base Account', image: COINBASE_ICON },
  { wallet: 'trust', label: 'Trust Wallet', image: TRUST_WALLET_ICON },
  { wallet: 'okx', label: 'OKX Wallet', mark: 'OKX' },
  { wallet: 'binance', label: 'Binance Wallet', mark: 'BNB' },
  { wallet: 'rainbow', label: 'Rainbow', mark: 'RBW' },
  { wallet: 'ledger', label: 'Ledger Wallet', image: LEDGER_ICON },
]

export default function WalletModal({ openSettings }: { openSettings: () => void }) {
  const { open } = useAppKit()
  const { connect, isPending } = useAppKitWallet({ namespace: 'eip155' })
  const { activationState, tryActivation } = useActivationState()
  const isActivatingInjected =
    activationState.status === ActivationStatus.PENDING && activationState.connection.type === ConnectionType.INJECTED
  const activateMetaMask = useCallback(async () => {
    try {
      await reownAppKit.disconnect('eip155')
    } catch (error) {
      console.debug(`Reown disconnect before MetaMask direct activation failed: ${error}`)
    }
    await tryActivation(getConnection(ConnectionType.INJECTED), () => undefined)
  }, [tryActivation])

  return (
    <Wrapper data-testid="wallet-modal">
      <AutoRow justify="space-between" width="100%" marginBottom="16px">
        <ThemedText.SubHeader>Connect a wallet</ThemedText.SubHeader>
        <IconButton Icon={Settings} onClick={openSettings} data-testid="wallet-settings" />
      </AutoRow>
      <AutoColumn gap="16px">
        <ConnectionPanel>
          <OptionGrid data-testid="option-grid">
            <WalletOptionButton
              type="button"
              disabled={isPending}
              onClick={() => open({ view: 'Connect', namespace: 'eip155' })}
              data-testid="wallet-option-connect"
            >
              <IconSlot>
                <Link2 size={14} />
              </IconSlot>
              <WalletLabel>Connect</WalletLabel>
              <span />
            </WalletOptionButton>
            <WalletOptionButton
              type="button"
              disabled={isPending || isActivatingInjected}
              onClick={activateMetaMask}
              data-testid="wallet-option-metamask"
            >
              <IconSlot>
                <WalletLogo src={METAMASK_ICON} alt="" aria-hidden="true" />
              </IconSlot>
              <WalletLabel>MetaMask</WalletLabel>
              <span />
            </WalletOptionButton>
            {REOWN_WALLETS.map(({ wallet, label, image, mark }) => (
              <WalletOptionButton
                key={wallet}
                type="button"
                disabled={isPending}
                onClick={() => connect(wallet)}
                data-testid={`wallet-option-${wallet}`}
              >
                <IconSlot>
                  {image ? (
                    <WalletLogo src={image} alt="" aria-hidden="true" />
                  ) : (
                    <BrandMark aria-hidden="true">{mark}</BrandMark>
                  )}
                </IconSlot>
                <WalletLabel>{label}</WalletLabel>
                <span />
              </WalletOptionButton>
            ))}
            <WalletOptionButton
              type="button"
              disabled={isPending}
              onClick={() => open({ view: 'AllWallets', namespace: 'eip155' })}
            >
              <IconSlot>
                <Grid size={14} />
              </IconSlot>
              <WalletLabel>All wallets</WalletLabel>
              <span />
            </WalletOptionButton>
          </OptionGrid>
        </ConnectionPanel>
        <PrivacyPolicyWrapper>
          <PrivacyPolicyNotice />
        </PrivacyPolicyWrapper>
      </AutoColumn>
    </Wrapper>
  )
}
